import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import toast from 'react-hot-toast';

const DocumentUpload = ({ onUpload, accept = '.pdf,.jpg,.jpeg,.png,.docx', maxSize = 10, multiple = false, label = 'Upload Document' }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  const validateFile = (file) => {
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file?.size > maxSizeBytes) {
      toast?.error(`File size must be less than ${maxSize}MB`);
      return false;
    }
    const allowedTypes = accept?.split(',')?.map(t => t?.trim());
    const fileExt = '.' + file?.name?.split('.')?.pop()?.toLowerCase();
    const isValidExt = allowedTypes?.some(type => fileExt === type);
    if (!isValidExt) {
      toast?.error('Invalid file type. Allowed: ' + accept);
      return false;
    }
    return true;
  };

  const handleFiles = (fileList) => {
    const fileArray = Array.from(fileList);
    const validFiles = fileArray?.filter(f => validateFile(f));
    if (validFiles?.length > 0) {
      setFiles(multiple ? [...files, ...validFiles] : validFiles);
    }
  };

  const handleFileSelect = (e) => {
    if (e?.target?.files) handleFiles(e?.target?.files);
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') setDragActive(true);
    else if (e?.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    if (e?.dataTransfer?.files) handleFiles(e?.dataTransfer?.files);
  };

  const removeFile = (index) => {
    setFiles(files?.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files?.length === 0) return;
    setUploading(true);
    setProgress(0);

    // Simulate upload progress
    for (let i = 0; i < files?.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 300));
      setProgress(Math.round(((i + 1) / files?.length) * 100));
    }

    const uploadedFiles = files?.map(file => ({
      name: file?.name,
      path: `uploads/${Date.now()}-${file?.name}`,
      size: file?.size,
      type: file?.type,
    }));

    setUploading(false);
    toast?.success('Upload Complete!');
    if (onUpload) onUpload(uploadedFiles);
    setFiles([]);
    setProgress(0);
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png']?.includes(ext)) return 'Image';
    if (ext === 'pdf') return 'FileText';
    if (ext === 'docx') return 'FileText';
    return 'File';
  };

  return (
    <div className="space-y-4">
      <label className="text-sm font-medium text-foreground font-caption block">{label}</label>
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          dragActive ? 'border-accent bg-accent/10' : 'border-border hover:border-accent'
        }`}
      >
        <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileSelect} className="hidden" multiple={multiple} disabled={uploading} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" disabled={uploading} />

        <div className="h-16 w-16 rounded-lg bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Icon name="Upload" size={32} color="var(--color-accent)" />
        </div>
        <p className="text-sm font-medium text-foreground mb-2">Drag and drop files here</p>
        <p className="text-xs text-muted-foreground mb-4">or</p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" size="sm" iconName="FolderOpen" iconPosition="left" onClick={() => fileInputRef?.current?.click()} disabled={uploading}>
            Browse Files
          </Button>
          <Button variant="outline" size="sm" iconName="Camera" iconPosition="left" onClick={() => cameraInputRef?.current?.click()} disabled={uploading}>
            Take Photo
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          {accept?.toUpperCase()?.replace(/\./g, '')?.replace(/,/g, ', ')} up to {maxSize}MB
        </p>
      </div>

      {files?.length > 0 && (
        <div className="space-y-3">
          {files?.map((file, index) => (
            <div key={index} className="p-4 rounded-lg bg-input border border-border">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={getFileIcon(file?.name)} size={20} color="var(--color-accent)" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{file?.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(file?.size)}</p>
                </div>
                <button onClick={() => removeFile(index)} className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center transition-smooth" disabled={uploading}>
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
            </div>
          ))}

          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Uploading...</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 bg-input rounded-full overflow-hidden">
                <div className="h-full bg-accent transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {!uploading && (
            <Button variant="default" fullWidth iconName="Upload" iconPosition="left" onClick={handleUpload}>
              Upload {files?.length} file{files?.length > 1 ? 's' : ''}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;